/**
 * Code Dictionary Decoder Module
 *
 * Translates integer codes from the Journal API into human-readable labels.
 * This module is self-contained with its own lookup tables (hex values are
 * optimized for chart visibility and differ from the shared constants).
 *
 * All decoder functions return a fallback value for unknown codes without throwing.
 */

// ─── Lookup Tables ───────────────────────────────────────────────────────────

const FEEDING_TYPES: Record<number, string> = {
  1: 'Normal',
  2: 'NGT',
  3: 'Peg Tube',
};

const MEAL_CATEGORIES: Record<number, string> = {
  1: 'Breakfast',
  2: 'Lunch',
  3: 'Tea Time',
  4: 'Dinner',
  5: 'Fruits',
  6: 'Snack',
};

const OUTPUT_CATEGORIES: Record<number, string> = {
  1: 'Stool',
  2: 'Urine',
};

const STOOL_TYPES: Record<number, string> = {
  1: 'Normal',
  2: 'Diaper',
  3: 'Colostomy',
  4: 'Ileostomy',
};

const URINE_TYPES: Record<number, string> = {
  1: 'Normal',
  2: 'Diaper',
  3: 'CBD',
  4: 'Urostomy',
};

const AMOUNTS: Record<number, { label: string; score: number }> = {
  1: { label: 'Large', score: 3 },
  2: { label: 'Moderate', score: 2 },
  3: { label: 'Small', score: 1 },
};

const BRISTOL_SCALE: Record<number, { label: string; description: string }> = {
  1: { label: 'Type 1', description: 'Separate hard lumps (hard to pass)' },
  2: { label: 'Type 2', description: 'Lumpy, sausage-shaped' },
  3: { label: 'Type 3', description: 'Sausage-shaped with cracks' },
  4: { label: 'Type 4', description: 'Smooth, soft sausage or snake' },
  5: { label: 'Type 5', description: 'Soft blobs with clear-cut edges' },
  6: { label: 'Type 6', description: 'Fluffy, mushy pieces' },
  7: { label: 'Type 7', description: 'Watery, no solid pieces' },
};

// Hex values optimized for chart visibility (per design document)
const STOOL_COLORS: Record<number, { label: string; hex: string }> = {
  1: { label: 'Black', hex: '#3D3D3D' },
  2: { label: 'Brown', hex: '#7A4414' },
  3: { label: 'Light Brown', hex: '#D69251' },
  4: { label: 'Green', hex: '#4B9538' },
  5: { label: 'Red', hex: '#8F1E22' },
  6: { label: 'Pink/Purple', hex: '#9F005C' },
  7: { label: 'Grey', hex: '#999788' },
  8: { label: 'Yellow', hex: '#FCEF24' },
  9: { label: 'Orange', hex: '#F45A28' },
};

const URINE_COLORS: Record<number, { label: string; hex: string }> = {
  1: { label: 'Clear', hex: '#F0F8FF' },
  2: { label: 'Light Yellow', hex: '#FFFFE0' },
  3: { label: 'Yellow', hex: '#FFD700' },
  4: { label: 'Dark Yellow', hex: '#DAA520' },
  5: { label: 'Amber', hex: '#FFBF00' },
  6: { label: 'Brown', hex: '#8B4513' },
  7: { label: 'Red', hex: '#DC143C' },
};

// ─── Amount Inversion Map ────────────────────────────────────────────────────

const AMOUNT_INVERSION: Record<number, number> = {
  1: 3,
  2: 2,
  3: 1,
};

// ─── Decoder Functions ───────────────────────────────────────────────────────

/**
 * Decode feeding type code to label.
 * 1=Normal, 2=NGT, 3=Peg Tube
 */
export function decodeFeedingType(code: number): string {
  return FEEDING_TYPES[code] ?? `Unknown (${code})`;
}

/**
 * Decode meal category code to label.
 * 1=Breakfast, 2=Lunch, 3=Tea Time, 4=Dinner, 5=Fruits, 6=Snack
 */
export function decodeMealCategory(code: number): string {
  return MEAL_CATEGORIES[code] ?? `Unknown (${code})`;
}

/**
 * Decode output category code to label.
 * 1=Stool, 2=Urine
 */
export function decodeOutputCategory(code: number): string {
  return OUTPUT_CATEGORIES[code] ?? `Unknown (${code})`;
}

/**
 * Decode stool type code to label.
 * 1=Normal, 2=Diaper, 3=Colostomy, 4=Ileostomy
 */
export function decodeStoolType(code: number): string {
  return STOOL_TYPES[code] ?? `Unknown (${code})`;
}

/**
 * Decode urine type code to label.
 * 1=Normal, 2=Diaper, 3=CBD, 4=Urostomy
 */
export function decodeUrineType(code: number): string {
  return URINE_TYPES[code] ?? `Unknown (${code})`;
}

/**
 * Decode amount code to label and inverted score.
 * 1=Large (score 3), 2=Moderate (score 2), 3=Small (score 1)
 *
 * The score inversion ensures "Large" appears highest on charts.
 */
export function decodeAmount(code: number): { label: string; score: number } {
  return AMOUNTS[code] ?? { label: `Unknown (${code})`, score: 0 };
}

/**
 * Decode Bristol Stool Scale code to label and description.
 * Codes 1-7 map to Type 1 through Type 7.
 */
export function decodeBristolScale(code: number): { label: string; description: string } {
  return BRISTOL_SCALE[code] ?? { label: `Unknown (${code})`, description: `Unknown (${code})` };
}

/**
 * Decode stool color code to label and hex value.
 * Hex values are optimized for chart visibility.
 */
export function decodeStoolColor(code: number): { label: string; hex: string } {
  return STOOL_COLORS[code] ?? { label: `Unknown (${code})`, hex: '#000000' };
}

/**
 * Decode urine color code to label and hex value.
 */
export function decodeUrineColor(code: number): { label: string; hex: string } {
  return URINE_COLORS[code] ?? { label: `Unknown (${code})`, hex: '#000000' };
}

/**
 * Invert amount code for chart y-axis positioning.
 * Maps 1→3, 2→2, 3→1 so that "Large" appears highest.
 *
 * This is an involution: invertAmount(invertAmount(c)) === c
 * for all valid codes in {1, 2, 3}.
 */
export function invertAmount(code: number): number {
  return AMOUNT_INVERSION[code] ?? code;
}
